import { getConfiguration } from '../../../services/configurationManager';
import { parseHeartRateData } from '../../../utils/hrDataRepair';
import type {
  SpectralAnalysisSnapshot,
  ValidationInputSession,
} from '../types';
import { runSpectralAnalysis } from '../utils/spectralAnalysis';

export type VerificationHrDataSource = 'hrData_repaired' | 'hrData';

export const getVerificationHrDataSource = (
  session: Pick<ValidationInputSession, 'hrData' | 'hrData_repaired'>
): VerificationHrDataSource =>
  parseHeartRateData(session.hrData_repaired).length > 0
    ? 'hrData_repaired'
    : 'hrData';

export const rebuildSnapshotFromSessions = (
  snapshot: SpectralAnalysisSnapshot,
  sessions: ValidationInputSession[]
): {
  snapshot: SpectralAnalysisSnapshot;
  dataSourcesBySessionId: Record<number, VerificationHrDataSource>;
} => {
  const selectedSessions = sessions.filter(session =>
    snapshot.selectedSessions.includes(session.id)
  );
  const dataSourcesBySessionId = Object.fromEntries(
    selectedSessions.map(session => [
      session.id,
      getVerificationHrDataSource(session),
    ])
  ) as Record<number, VerificationHrDataSource>;

  const pipeline = runSpectralAnalysis(selectedSessions);

  return {
    snapshot: {
      ...snapshot,
      excludedSessions: pipeline.excludedSessions,
      includedSessions: pipeline.validations.map(validation => {
        const spectral = pipeline.spectralResults.find(
          result => result.sessionId === validation.sessionId
        );

        if (!spectral) {
          throw new Error(
            `Missing spectral result for session ${validation.sessionId}`
          );
        }

        return { validation, spectral };
      }),
      groupSummary: pipeline.groupSummary,
    },
    dataSourcesBySessionId,
  };
};

export const resolveVerificationSnapshot = async (
  snapshot: SpectralAnalysisSnapshot
): Promise<{
  snapshot: SpectralAnalysisSnapshot;
  dataSourcesBySessionId: Record<number, VerificationHrDataSource>;
}> => {
  const sessions = await Promise.all(
    snapshot.selectedSessions.map(async sessionId => {
      const configuration = await getConfiguration(sessionId);
      return configuration.data as ValidationInputSession;
    })
  );

  return rebuildSnapshotFromSessions(snapshot, sessions);
};

export const countSessionsByDataSource = (
  dataSourcesBySessionId: Record<number, VerificationHrDataSource>
) => {
  const values = Object.values(dataSourcesBySessionId);
  return {
    hrDataRepaired: values.filter(source => source === 'hrData_repaired')
      .length,
    hrData: values.filter(source => source === 'hrData').length,
  };
};
